import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { AuthProvider } from './auth/AuthContext'
import type { BootstrapResult } from './auth/bootstrap'
import { BrandingProvider } from './brand/BrandingProvider'
import type { BrandingRecord } from './api/types'
import { AppShell } from './components/AppShell'
import { SignIn } from './screens/SignIn'
import { ThemeProvider } from './theme/ThemeProvider'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
})

/* Routes per docs/architecture.md §7. Each screen is its own chunk (route-level code splitting). */
const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, lazy: () => import('./screens/Home').then((m) => ({ Component: m.Home })) },
      {
        path: 'cattle',
        lazy: () => import('./screens/Cattle').then((m) => ({ Component: m.Cattle })),
      },
      {
        path: 'cattle/chute',
        lazy: () => import('./screens/Chute').then((m) => ({ Component: m.Chute })),
      },
      {
        path: 'cattle/calving',
        lazy: () => import('./screens/Chute').then((m) => ({ Component: m.Calving })),
      },
      {
        path: 'cattle/breeding',
        lazy: () => import('./screens/Breeding').then((m) => ({ Component: m.Breeding })),
      },
      {
        path: 'cattle/marketing',
        lazy: () => import('./screens/Marketing').then((m) => ({ Component: m.Marketing })),
      },
      {
        path: 'cattle/move',
        lazy: () => import('./screens/Chute').then((m) => ({ Component: m.MoveHerd })),
      },
      {
        path: 'cattle/uploads',
        lazy: () => import('./screens/Uploads').then((m) => ({ Component: m.Uploads })),
      },
      {
        path: 'cattle/missing',
        lazy: () => import('./screens/Missing').then((m) => ({ Component: m.MissingCattle })),
      },
      {
        path: 'pastures',
        lazy: () => import('./screens/Pastures').then((m) => ({ Component: m.Pastures })),
      },
      { path: 'hay', lazy: () => import('./screens/Hay').then((m) => ({ Component: m.Hay })) },
      {
        path: 'map',
        lazy: () => import('./screens/MapScreen').then((m) => ({ Component: m.MapScreen })),
      },
      {
        path: 'leases',
        lazy: () => import('./screens/Leases').then((m) => ({ Component: m.Leases })),
      },
      {
        path: 'tasks',
        lazy: () => import('./screens/Tasks').then((m) => ({ Component: m.Tasks })),
      },
      {
        path: 'records',
        lazy: () => import('./screens/Records').then((m) => ({ Component: m.Records })),
      },
      {
        path: 'questions',
        lazy: () => import('./screens/Questions').then((m) => ({ Component: m.Questions })),
      },
      {
        path: 'finance',
        lazy: () => import('./screens/Finance').then((m) => ({ Component: m.Finance })),
      },
      {
        path: 'reports',
        lazy: () => import('./screens/Reports').then((m) => ({ Component: m.Reports })),
      },
      {
        path: 'settings',
        lazy: () => import('./screens/Settings').then((m) => ({ Component: m.Settings })),
      },
      {
        path: 'settings/brand',
        lazy: () => import('./screens/SettingsBrand').then((m) => ({ Component: m.SettingsBrand })),
      },
      {
        path: 'about',
        lazy: () => import('./screens/About').then((m) => ({ Component: m.About })),
      },
      {
        path: 'notes',
        lazy: () => import('./screens/Notes').then((m) => ({ Component: m.Notes })),
      },
      {
        path: 'admin',
        lazy: () => import('./screens/Admin').then((m) => ({ Component: m.Admin })),
      },
      {
        path: '*',
        lazy: () => import('./screens/NotFound').then((m) => ({ Component: m.NotFound })),
      },
    ],
  },
])

export function App({
  result,
  branding,
}: {
  result: BootstrapResult
  branding: BrandingRecord | null
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <BrandingProvider initial={branding ?? undefined}>
        {result.mode === 'login' || result.mode === 'unreachable' ? (
          <SignIn result={result} />
        ) : (
          <AuthProvider result={result}>
            <ThemeProvider>
              <RouterProvider router={router} />
            </ThemeProvider>
          </AuthProvider>
        )}
      </BrandingProvider>
    </QueryClientProvider>
  )
}
